// ** React
import { useState, useEffect, useRef } from "react";
import Link from "next/link";

// ** Hooks
import { useSearch } from "@/hooks/useSearch";

// ** MUI Imports
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Card, CardContent, Modal, Typography } from "@mui/material";

// ** Styles
import styles from "./SearchInput.module.css";

const SearchInput = ({ open, onOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { results, loading } = useSearch(searchTerm);

  const modalInputRef = useRef(null);
  const outerInputRef = useRef(null);

  const handlecClose = () => {
    setSearchTerm("");
    onClose();
  };

  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        requestAnimationFrame(() => {
          if (modalInputRef.current) {
            modalInputRef.current.focus();
          }
        });
      }, 150);

      return () => clearTimeout(timeout);
    }

    if (!open && outerInputRef.current) {
      outerInputRef.current.blur();
    }
  }, [open]);

  return (
    <>
      <Box sx={{ position: "relative" }} onClick={onOpen}>
        <input
          ref={outerInputRef}
          name="searchInput"
          placeholder="Discover news, articles and more..."
          className={styles.searchInput}
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
        ></input>
        <SearchIcon
          sx={{
            position: "absolute",
            color: "rgb(113, 128, 150)",
            fontWeight: 900,
            fontSize: "22px",
            top: "14px",
            left: "16px",
          }}
        ></SearchIcon>
      </Box>
      <Modal
        open={open}
        onClose={handlecClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.6)", // Adjust the alpha for transparency
          padding: "0px 20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "25px",
            }}
          >
            <Box sx={{ position: "relative", width: "80%", maxWidth: "800px" }}>
              <input
                name="searchInput"
                placeholder="Discover news, articles and more..."
                className={styles.searchInputModal}
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
                ref={modalInputRef}
              ></input>
              <SearchIcon
                sx={{
                  position: "absolute",
                  color: "rgb(113, 128, 150)",
                  fontWeight: 900,
                  fontSize: "26px",
                  top: "16px",
                  left: "16px",
                }}
              ></SearchIcon>
            </Box>
            <CancelIcon
              sx={{
                color: "#e2e8f0",
                fontWeight: 900,
                fontSize: "38px",
                marginLeft: "100px",
                cursor: "pointer",
              }}
              onClick={handlecClose}
            ></CancelIcon>
          </Box>
          <Card
            sx={{
              width: "100%",
              maxWidth: "1200px",
              marginTop: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
              margin: "20px auto",
            }}
          >
            <CardContent sx={{ padding: "0px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  overflowY: "scroll",
                  maxHeight: "80vh",
                  padding: "20px",
                  paddingRight: "30px",
                  paddingBottom: "0px",
                }}
              >
                {loading && searchTerm.length > 0 ? (
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 900, color: "rgb(113, 128, 150)" }}
                  >
                    Searching...
                  </Typography>
                ) : Object.entries(results).length === 0 &&
                  searchTerm.length === 0 ? (
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 900, color: "rgb(113, 128, 150)" }}
                  >
                    Search a blog post
                  </Typography>
                ) : Object.entries(results).length === 0 ? (
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 900, color: "rgb(113, 128, 150)" }}
                  >
                    No results for `{searchTerm}`
                  </Typography>
                ) : (
                  Object.entries(results).map(([tech, articles]) => (
                    <Box key={tech}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 600,
                          color: "rgb(45, 55, 72)",
                          fontSize: { xs: "18px", md: "22px" },
                        }}
                      >
                        {tech}
                      </Typography>
                      {articles.map((article, index) => (
                        <Box
                          key={article.id}
                          marginTop="10px"
                          marginLeft="10px"
                          marginBottom={
                            index === articles.length - 1 ? "15px" : "10px"
                          }
                          paddingBottom={
                            index === articles.length - 1 ? "10px" : "0px"
                          }
                          borderBottom={
                            index === articles.length - 1
                              ? "1px solid #e2e8f0"
                              : "none"
                          }
                        >
                          <Typography
                            component={Link}
                            href={`/blog/article/${article.slug}`}
                            variant="h6"
                            onClick={handlecClose}
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: "16px", md: "20px" },
                              color: "rgb(102, 126, 234)",
                              marginBottom: "8px",
                              textDecoration: "none",
                            }}
                          >
                            {article.name}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 400,
                              color: "rgb(113, 128, 150)",
                              fontSize: { xs: "14px", md: "18px" },
                            }}
                          >
                            {article.shortDescription}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Modal>
    </>
  );
};

export default SearchInput;
